/**
 * Reply queue (#468) — pure pacing logic for the tablet's BertaReplyPanel.
 *
 * Why this exists:
 *   The server (`app/api/voice.py`) streams the LLM reply as `reply_chunk`
 *   events, one per sentence. The LLM finishes in ~2 seconds; TTS audio for
 *   the same reply plays over 30-60 seconds. Pre-#468 the tablet appended
 *   every chunk to the visible reply immediately, so the user saw a wall
 *   of text long before they heard most of it (Bas's #468 story-test
 *   complaint, follow-on to #466's wide-screen polish).
 *
 * What this does:
 *   Buffer incoming chunks in an internal queue. Reveal sentence-at-a-time
 *   on a timer paced at ~150 wpm (the slow end of typical TTS so the
 *   reveal tracks at / just BEHIND the voice and never leads — #1032).
 *   The first chunk in a fresh reply reveals immediately so the panel
 *   never looks dead; subsequent chunks pace. `flush()` reveals whatever
 *   is left the instant she stops speaking, so the full reply is there to
 *   re-read instead of trailing in after her voice.
 *
 * #1032 (the "running ahead" fix):
 *   Pre-#1032 the pace was 175 wpm — deliberately AHEAD of the audio. On a
 *   long reply that lead accumulated, and with the panel auto-scrolled to
 *   the newest revealed line the user read the part she was ABOUT to say,
 *   not the part she was saying (Bas's tablet repro). Lowering the pace
 *   below the TTS rate makes the reveal a follower, not a leader. This is
 *   the cheap, display-only half of the fix; the audio-accurate sync that
 *   reads real playback timing is a separate, deferred story.
 *
 * Why a pure factory (not a React hook directly):
 *   This file is testable without React/jsdom. The hook in
 *   `web/src/hooks/useProgressiveReply.js` is a thin shim that wires
 *   `onChange` to React state.
 *
 * Why option B (time-based) instead of A (server-side audio_start):
 *   A "nice to have" per Bas's chat 2026-05-08. Time-based gets ~90% of
 *   the visual benefit with no server churn. If real-tablet testing shows
 *   the drift bothers him we can layer a `tts_sentence_start` WS event on
 *   top later — `enqueue` is already the right hook point.
 */

// #1032 — was 175 ("slightly ahead" of TTS, #468). Dropped to 150 (the
// slow end of the typical 150-160 wpm range) so the reveal tracks at /
// just behind the voice and never leads. Targets the default speech rate;
// per-user speech_rate (#498) is not plumbed to the tablet, so extreme
// rate settings can still drift — the deferred audio-accurate sync story
// fixes that by driving the reveal from real playback timing.
const DEFAULT_WPM = 150;
const DEFAULT_MIN_MS = 800;    // floor so a 2-word sentence still feels paced
const DEFAULT_MAX_MS = 6000;   // ceiling so a single huge "sentence" doesn't stall

function countWords(s) {
  return s.trim().split(/\s+/).filter(Boolean).length || 1;
}

/**
 * Compute the pacing delay (ms) for a sentence at the given words-per-minute
 * rate. Bounded by [minMs, maxMs] so very short / very long sentences still
 * feel right. Exported for unit testing.
 */
export function paceMsForSentence(text, wpm = DEFAULT_WPM, minMs = DEFAULT_MIN_MS, maxMs = DEFAULT_MAX_MS) {
  const words = countWords(text);
  const ms = (words / (wpm / 60)) * 1000;
  return Math.max(minMs, Math.min(maxMs, ms));
}

/**
 * Create a reply queue.
 *
 * @param {Object} opts
 * @param {Function} opts.onChange — called with the new visible reply string
 *                   whenever it changes. (React hook wires this to setState.)
 * @param {number}   [opts.wpm=175] — pacing rate.
 * @param {number}   [opts.minMs=800] — floor on sentence dwell time.
 * @param {number}   [opts.maxMs=6000] — ceiling on sentence dwell time.
 * @param {Function} [opts.scheduleTimer=setTimeout] — injectable for tests.
 * @param {Function} [opts.cancelTimer=clearTimeout] — injectable for tests.
 *
 * @returns {{enqueue: Function, setReply: Function, dispose: Function, _state: Function}}
 */
export function createReplyQueue({
  onChange,
  wpm = DEFAULT_WPM,
  minMs = DEFAULT_MIN_MS,
  maxMs = DEFAULT_MAX_MS,
  scheduleTimer = setTimeout,
  cancelTimer = clearTimeout,
} = {}) {
  if (typeof onChange !== 'function') {
    throw new Error('createReplyQueue: onChange callback required');
  }

  let queue = [];
  let timerId = null;
  let visible = '';

  function emit() {
    onChange(visible);
  }

  function tick() {
    timerId = null;
    if (queue.length === 0) return;
    const next = queue.shift();
    visible = visible ? visible + ' ' + next : next;
    emit();
    // Always schedule the pacing wait. If queue is empty when the timer
    // fires, tick() returns without emitting — but the wait still acts as
    // a back-pressure window: a chunk arriving during the wait is queued,
    // and the next tick picks it up. A chunk arriving AFTER the wait
    // expired sees timerId === null and triggers tick() immediately
    // (natural pause already taken).
    const ms = paceMsForSentence(next, wpm, minMs, maxMs);
    timerId = scheduleTimer(tick, ms);
  }

  return {
    /** Append a chunk. Reveals immediately if no timer is running. */
    enqueue(text) {
      if (!text) return;
      queue.push(text);
      if (timerId === null) {
        tick();
      }
    },

    /**
     * Overwrite the visible reply. Always cancels pending chunks + timer.
     * Used for: final `reply` event, toast messages ("Call ended",
     * reassurance), and clear-on-new-turn.
     */
    setReply(text) {
      if (timerId !== null) {
        cancelTimer(timerId);
        timerId = null;
      }
      queue = [];
      visible = text || '';
      emit();
    },

    /**
     * Reveal every queued sentence immediately and stop the pacing timer,
     * KEEPING the already-visible text (unlike setReply, which overwrites).
     * Called when the companion has finished speaking (#1032) so the full
     * reply lands at once for re-reading, instead of the last sentence or
     * two trailing in seconds after her voice has already stopped.
     * No-op when nothing is queued.
     */
    flush() {
      if (timerId !== null) {
        cancelTimer(timerId);
        timerId = null;
      }
      if (queue.length === 0) return;
      const rest = queue.join(' ');
      queue = [];
      visible = visible ? visible + ' ' + rest : rest;
      emit();
    },

    /** Cancel any pending timer; call from React useEffect cleanup. */
    dispose() {
      if (timerId !== null) {
        cancelTimer(timerId);
        timerId = null;
      }
    },

    /** For tests only — peek at internal state. */
    _state() {
      return {
        queueLen: queue.length,
        visible,
        hasTimer: timerId !== null,
      };
    },
  };
}
